import { keyframes } from '@mui/system';

import { colors, fonts } from '../../theme';

// The spinner only shows when the wait lasts: a preloaded video starts before it appears.
const appear = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const reset = {
  p: 0,
  border: 0,
  backgroundColor: 'transparent',
  fontFamily: fonts.text,
  cursor: 'pointer',
};

const fill = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
};

const styles = {
  frame: {
    position: 'relative',
    height: '100%',
    aspectRatio: '9 / 16',
    maxWidth: '100%',
    flexShrink: 0,
    borderRadius: '14px',
    overflow: 'hidden',
    backgroundColor: colors.background,
    cursor: 'pointer',
  },
  blur: {
    ...fill,
    objectFit: 'cover',
    filter: 'blur(22px) brightness(0.45)',
    transform: 'scale(1.25)',
  },
  video: {
    ...fill,
    objectFit: 'cover',
  },
  videoLandscape: {
    ...fill,
    objectFit: 'contain',
  },
  sound: {
    position: 'absolute',
    top: 14,
    right: 14,
    width: 36,
    height: 36,
    color: colors.text,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
    },
    '& svg': {
      fontSize: 19,
    },
  },
  buffering: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    mt: '-17px',
    ml: '-17px',
    color: 'rgba(255, 255, 255, 0.8)',
    pointerEvents: 'none',
    animation: `${appear} 200ms ease-out 300ms both`,
  },
  paused: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 64,
    height: 64,
    mt: '-32px',
    ml: '-32px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    '& svg': {
      fontSize: 38,
      color: colors.text,
    },
  },
  info: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    pt: 7.5,
    px: 2,
    pb: 2.25,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.25,
    background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.85))',
  },
  userRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    minWidth: 0,
  },
  username: {
    ...reset,
    color: colors.text,
    fontSize: 14,
    fontWeight: 700,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  description: {
    m: 0,
    fontFamily: fonts.text,
    fontSize: 14,
    lineHeight: 1.4,
    color: colors.text,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    overflowWrap: 'anywhere',
  },
  // Taller than the bar: easier to catch with the mouse. The bar grows on hover.
  seek: {
    py: 1,
    my: -1,
    cursor: 'pointer',
    touchAction: 'none',
    '&:hover .seek-track': {
      height: 6,
    },
  },
  progress: {
    height: 3,
    transition: 'height 120ms ease',
    borderRadius: '2px',
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    overflow: 'hidden',
  },
  progressValue: {
    display: 'block',
    height: '100%',
    backgroundColor: colors.text,
  },
};

export default styles;
